import React, { useRef, useState } from "react";
import { Camera, RotateCcw, SkipForward } from "lucide-react";
import { ocrReceipt } from "@/lib/api";
import { toast } from "sonner";

// Downscale + JPEG-encode an image File to keep base64 small.
export async function fileToCompressedBase64(file, maxDim = 1600, quality = 0.82) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  let { width: w, height: h } = img;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  w = Math.round(w * scale);
  h = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function PhotoStep({ kind, till, onResult, onSkip, testIdPrefix = "photo", allowSkip = true }) {
  const fileRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await fileToCompressedBase64(file);
      setPhoto(b64);
      const result = await ocrReceipt(kind, b64, till);
      onResult({ photo: b64, ocr: result });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't read photo. You can type the numbers instead.");
      // Still pass photo so user can confirm manually
      onResult({ photo: photo, ocr: {} });
    } finally {
      setBusy(false);
    }
  };

  const retake = () => {
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div data-testid={`${testIdPrefix}-step`} className="space-y-4">
      <input
        ref={fileRef}
        data-testid={`${testIdPrefix}-input`}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
      />
      {photo ? (
        <div className="rounded-xl overflow-hidden border border-[#E8E4DA] bg-white">
          <img src={photo} alt="Receipt" className="w-full max-h-72 object-contain bg-[#F5F2EA]" />
        </div>
      ) : (
        <button
          data-testid={`${testIdPrefix}-take`}
          onClick={pick}
          disabled={busy}
          className="w-full h-48 rounded-2xl border-2 border-dashed border-[#1A5D3A]/40 bg-white hover:bg-[#F5F2EA] flex flex-col items-center justify-center gap-2 text-[#2B533E] active:scale-[0.99] disabled:opacity-50 px-6"
        >
          <Camera size={28} />
          <div className="font-medium" style={{ fontFamily: "'Fraunces', serif" }}>
            {busy ? "Reading…" : "Tap here — your camera opens"}
          </div>
          <div className="text-xs text-[#5C7566] text-center">
            Lay the receipt flat, fit the whole thing in the shot, and snap. The numbers get read for you — you'll check them on the next screen.
          </div>
        </button>
      )}
      <div className="flex gap-2">
        {photo && (
          <button
            data-testid={`${testIdPrefix}-retake`}
            onClick={retake}
            disabled={busy}
            className="flex-1 h-12 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RotateCcw size={16} /> Retake
          </button>
        )}
        {allowSkip && (
          <button
            data-testid={`${testIdPrefix}-skip`}
            onClick={onSkip}
            disabled={busy}
            className="flex-1 h-12 rounded-xl bg-white border border-[#E8E4DA] text-[#2B533E] text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <SkipForward size={16} /> Skip photo, type numbers
          </button>
        )}
      </div>
      {busy && (
        <div data-testid={`${testIdPrefix}-busy`} className="text-center text-[#5C7566] text-sm">
          Reading the receipt… you'll confirm every number on the next screen.
        </div>
      )}
      {!photo && !busy && (
        <p className="text-[11px] text-[#5C7566] text-center">
          {allowSkip
            ? "Even if you'd rather type the numbers, please take the photo first — we keep it for our records."
            : "This photo is required — we keep it for the records. You can still type or correct the numbers below after."}
        </p>
      )}
    </div>
  );
}
